/**
 * iter354 — single source of truth for the stored daily macro goals.
 *
 * These helpers used to live inside `useNutritionData`, so the goals could only
 * be recomputed while the Nutrition page was mounted. Changing the weight goal
 * anywhere else (Personal dialog, Planner setup) left the SERVER copy of
 * `nutrition_goals` on the previous goal's numbers — which is what the trainer
 * dashboard and the AI Analyzer read.
 */
import { computeDailyNutritionGoals } from './nutritionGoals';
import { STORAGE_KEYS } from './nutrition/constants';
import { getToken } from './tokenStorage';
import { sendOrQueue } from './offlineQueue';

export const NUTRITION_GOALS_KEY = STORAGE_KEYS.NUTRITION_GOALS;
export const GOALS_FORMULA_VERSION = 2;

export interface NutritionGoals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  /** Which macro formula produced these numbers. */
  formulaVersion?: number;
  /** Fingerprint of the body-profile inputs the numbers were derived from. */
  profileSig?: string;
  /** Set when the user hand-edits the goals. */
  customMacros?: boolean;
  /** The weight goal that was active when the goals were hand-edited —
   *  hand-set maintenance macros must not survive a switch to Mini-Cut. */
  customMacrosGoal?: string;
  customFiber?: boolean;
}

export const DEFAULT_GOALS: NutritionGoals = {
  calories: 2000,
  protein: 150,
  carbs: 250,
  fat: 65,
  fiber: 25,
};

/** Fingerprint of every Body Info input the macro formula depends on. */
export function bodyProfileSignature(adaptiveMaintenance?: number | null): string {
  return [
    localStorage.getItem('gym_notebook_persistent_bodyweight') || '',
    localStorage.getItem('gym_notebook_persistent_weight_unit') || '',
    localStorage.getItem('gym_notebook_persistent_height') || '',
    localStorage.getItem('gym_notebook_persistent_height_unit') || '',
    localStorage.getItem('gym_notebook_persistent_age') || '',
    localStorage.getItem('gym_notebook_persistent_gender') || '',
    localStorage.getItem('gym_notebook_persistent_body_fat') || '',
    localStorage.getItem('gym_notebook_weight_goal') || '',
    localStorage.getItem('gym_notebook_weight_goal_start') || '',
    adaptiveMaintenance && adaptiveMaintenance > 0 ? Math.round(adaptiveMaintenance) : '',
  ].join('|');
}

/** Auto-calculate macro goals from Personal body info + weight goal. Delegates
 *  to the shared formula so the trainer's view of the same client produces
 *  identical numbers. */
export function computeGoalsFromProfile(adaptiveMaintenance?: number | null): NutritionGoals | null {
  const weight = parseFloat(localStorage.getItem('gym_notebook_persistent_bodyweight') || '0');
  const weightUnit = localStorage.getItem('gym_notebook_persistent_weight_unit') || 'kg';
  const height = parseFloat(localStorage.getItem('gym_notebook_persistent_height') || '0');
  const heightUnit = localStorage.getItem('gym_notebook_persistent_height_unit') || 'cm';
  const age = parseInt(localStorage.getItem('gym_notebook_persistent_age') || '0');
  const gender = localStorage.getItem('gym_notebook_persistent_gender') || 'male';
  const weightGoal = localStorage.getItem('gym_notebook_weight_goal') || 'maintain';
  const weightGoalStart = localStorage.getItem('gym_notebook_weight_goal_start') || '';
  const bodyFatPct = localStorage.getItem('gym_notebook_persistent_body_fat') || '';
  const miniCutDurationWeeks = localStorage.getItem('gym_notebook_mini_cut_duration_weeks') === '4' ? 4 : 2;
  const tdeeMode = (localStorage.getItem('gym_notebook_mini_cut_tdee_mode') === 'static' ? 'static' : 'adaptive') as 'static' | 'adaptive';

  const weightKg = weightUnit === 'lbs' ? weight * 0.453592 : weight;
  const heightCm = heightUnit === 'in' ? height * 2.54 : height;
  if (weightKg <= 0 || heightCm <= 0 || age <= 0) return null;

  const g = computeDailyNutritionGoals(
    { weight, weightUnit, height, heightUnit, age, gender, weightGoal, weightGoalStart, bodyFatPct, miniCutDurationWeeks: miniCutDurationWeeks as 2 | 4, tdeeMode },
    undefined,
    adaptiveMaintenance,
  );
  return {
    calories: g.calories,
    protein: g.protein,
    carbs: g.carbs,
    fat: g.fat,
    fiber: g.fiber,
    formulaVersion: GOALS_FORMULA_VERSION,
    profileSig: bodyProfileSignature(adaptiveMaintenance),
  };
}

/** Bring persisted goals up to date with the CURRENT formula + body profile.
 *  A hand-edited set survives while its own weight goal is still active; a
 *  user-chosen fiber target survives a macro recompute. Returns the same
 *  object when nothing had to change. */
export function reconcileGoals(
  saved: NutritionGoals & Record<string, any>,
  adaptiveMaintenance?: number | null,
): { goals: NutritionGoals; changed: boolean } {
  const currentGoal = (localStorage.getItem('gym_notebook_weight_goal') || 'maintain').toLowerCase();
  if (saved.customMacros) {
    // Legacy pins carry no stamp, so they count as 'maintain' and are only
    // dropped once a cut / gain block is running, where stale maintenance
    // macros actively mislead.
    const pinnedGoal = saved.customMacrosGoal
      ? String(saved.customMacrosGoal).toLowerCase()
      : 'maintain';
    if (pinnedGoal === currentGoal) return { goals: saved, changed: false };
  }
  const sig = bodyProfileSignature(adaptiveMaintenance);
  if (saved.formulaVersion === GOALS_FORMULA_VERSION && saved.profileSig === sig && !saved.customMacros) {
    return { goals: saved, changed: false };
  }
  const auto = computeGoalsFromProfile(adaptiveMaintenance);
  if (!auto) return { goals: saved, changed: false };
  return {
    goals: {
      ...saved,
      calories: auto.calories,
      protein: auto.protein,
      carbs: auto.carbs,
      fat: auto.fat,
      fiber: saved.customFiber ? saved.fiber : auto.fiber,
      formulaVersion: GOALS_FORMULA_VERSION,
      profileSig: sig,
      customMacros: false,
      customMacrosGoal: currentGoal,
    },
    changed: true,
  };
}

/** The Adaptive TDEE maintenance the Nutrition page would use, read from the
 *  same localStorage cache so both paths compute identical goals. */
export function cachedAdaptiveMaintenance(): number | null {
  try {
    const raw = localStorage.getItem('adaptive_tdee_cache_v1');
    if (!raw) return null;
    const { snapshot, settings } = JSON.parse(raw) || {};
    if (!snapshot || !settings) return null;
    if (!settings.auto_adapt_enabled || snapshot.fallback_used) return null;
    if (snapshot.confidence === 'low') return null;
    if (!snapshot.tdee_kcal || snapshot.tdee_kcal <= 0) return null;
    return Math.round(snapshot.tdee_kcal);
  } catch { return null; }
}

/** Recompute the stored goals for the CURRENT weight goal and push them to the
 *  server, so the trainer dashboard and the AI Analyzer see the new targets
 *  immediately instead of waiting for the client to open Nutrition.
 *  Queued automatically when offline. */
export async function refreshNutritionGoalsOnServer(): Promise<NutritionGoals | null> {
  const adaptive = cachedAdaptiveMaintenance();
  let next: NutritionGoals | null = null;
  try {
    const raw = localStorage.getItem(NUTRITION_GOALS_KEY);
    if (raw) {
      const { goals, changed } = reconcileGoals({ ...DEFAULT_GOALS, ...JSON.parse(raw) }, adaptive);
      if (!changed) return null;
      next = goals;
    } else {
      next = computeGoalsFromProfile(adaptive);
    }
  } catch { return null; }
  if (!next) return null;

  localStorage.setItem(NUTRITION_GOALS_KEY, JSON.stringify(next));
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  await sendOrQueue(
    `${process.env.REACT_APP_BACKEND_URL || ''}/api/data/nutrition-goals`,
    { method: 'PUT', headers, body: JSON.stringify({ goals: next }), credentials: 'include' as RequestCredentials },
    { label: 'Macro goals', dedupeKey: 'PUT:/api/data/nutrition-goals' },
  );
  return next;
}
